import { useState, useEffect } from "react";
import { X, Plus, AlertCircle, Loader2 } from "lucide-react";
import { createManualQuota, getUFValueForPeriod } from "../../../../services/quotasService";
import { useAvailableYears } from "../../../../hooks/useAvailableYears";

const CreateManualQuota = ({ isOpen, onClose, socio, onSuccess }) => {
  const [formData, setFormData] = useState({
    period: "",
    year: new Date().getFullYear(),
    ufValue: "",
  });
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [loadingUF, setLoadingUF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { availableYears } = useAvailableYears();

  // Obtener períodos válidos según la periodicidad del socio
  const getValidPeriods = () => {
    if (!socio?.periodicity) return [];
    
    const periodicity = socio.periodicity;
    if (periodicity === "Mensual") {
      return [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
      ];
    } else if (periodicity === "Semestral") {
      return ["Semestre 1", "Semestre 2"];
    } else if (periodicity === "Anual") {
      return ["Anual"];
    }
    return [];
  };

  const validPeriods = getValidPeriods();

  // Cargar automáticamente el valor UF desde la API cuando se selecciona período y año
  useEffect(() => {
    const loadUfValueFromAPI = async () => {
      // Necesitamos año y período seleccionados
      if (!formData.year || !formData.period) {
        setFormData((prev) => ({ ...prev, ufValue: "" }));
        setCalculatedAmount(null);
        return;
      }

      setLoadingUF(true);
      setError(null);
      
      try {
        const response = await getUFValueForPeriod(formData.period, formData.year);

        if (response.error || !response.success) {
          // Si hay error, dejamos el campo vacío para que el usuario lo ingrese manualmente
          setFormData((prev) => ({ ...prev, ufValue: "" }));
          setCalculatedAmount(null);
          if (response.message) {
            console.warn("No se pudo obtener UF automáticamente:", response.message);
          }
        } else if (response.ufValue) {
          // Si obtenemos el valor UF, lo asignamos y calculamos el monto
          const ufValue = response.ufValue;
          const companyAmount = Number(socio?.amount) || 0;
          const calculated = Math.round(companyAmount * ufValue);
          
          setFormData((prev) => ({
            ...prev,
            ufValue: String(ufValue),
          }));
          setCalculatedAmount(calculated);
        }
      } catch (e) {
        console.error("Error obteniendo valor UF desde API:", e);
        setFormData((prev) => ({ ...prev, ufValue: "" }));
        setCalculatedAmount(null);
      } finally {
        setLoadingUF(false);
      }
    };

    loadUfValueFromAPI();
  }, [formData.year, formData.period, socio?.amount]);

  // Recalcular monto cuando cambia el valor UF manualmente
  useEffect(() => {
    if (formData.ufValue && socio?.amount) {
      const ufValue = parseFloat(formData.ufValue);
      const companyAmount = Number(socio?.amount) || 0;
      if (!isNaN(ufValue) && ufValue > 0) {
        setCalculatedAmount(Math.round(companyAmount * ufValue));
      } else {
        setCalculatedAmount(null);
      }
    } else {
      setCalculatedAmount(null);
    }
  }, [formData.ufValue, socio?.amount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!formData.period || !formData.year || !formData.ufValue) {
      setError("Por favor completa todos los campos");
      return;
    }

    const ufValue = parseFloat(formData.ufValue);
    if (isNaN(ufValue) || ufValue <= 0) {
      setError("El valor de la UF debe ser un número mayor a 0");
      return;
    }

    setLoading(true);
    try {
      const response = await createManualQuota(
        socio.rut,
        formData.period,
        formData.year,
        ufValue
      );

      if (response.error) {
        setError(response.message || "Error al crear la cuota");
      } else {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || "Error al crear la cuota");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      period: "",
      year: new Date().getFullYear(),
      ufValue: "",
    });
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-[#0488AF] to-[#C42251] rounded-lg">
              <Plus className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Crear Cuota Manual</h2>
              <p className="text-sm text-gray-600">{socio?.business_name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="text-gray-500" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Información del socio */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm space-y-1">
              <p><span className="font-semibold">RUT:</span> {socio?.rut}</p>
              <p><span className="font-semibold">Periodicidad:</span> {socio?.periodicity}</p>
              <p><span className="font-semibold">Monto UF:</span> {socio?.amount} UF</p>
            </div>
          </div>

          {/* Período */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Período <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.period}
              onChange={(e) => setFormData({ ...formData, period: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
              required
            >
              <option value="">Selecciona un período</option>
              {validPeriods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
            {validPeriods.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No se pudo determinar la periodicidad del socio
              </p>
            )}
          </div>

          {/* Año */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Año <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
              required
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Valor UF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Valor UF <span className="text-red-500">*</span>
              {loadingUF && (
                <span className="ml-2 text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Consultando API...
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.ufValue}
              onChange={(e) => setFormData({ ...formData, ufValue: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
              placeholder="Se calculará automáticamente"
              required
              disabled={loadingUF}
            />
            <p className="text-xs text-gray-500 mt-1">
              Valor UF del día 5 del mes correspondiente (obtenido automáticamente desde la API)
            </p>
          </div>

          {/* Monto Calculado */}
          {calculatedAmount !== null && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm">
                <p className="font-semibold text-green-800 mb-1">Monto Calculado:</p>
                <p className="text-lg font-bold text-green-900">
                  ${calculatedAmount.toLocaleString('es-CL')} CLP
                </p>
                <p className="text-xs text-green-700 mt-1">
                  {socio?.amount} UF × {formData.ufValue ? parseFloat(formData.ufValue).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0'} = ${calculatedAmount.toLocaleString('es-CL')} CLP
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">¡Cuota creada exitosamente!</p>
                <p className="text-sm text-green-700">La cuota ha sido creada y estará disponible para el socio.</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#0488AF] to-[#C42251] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Creando..." : "Crear Cuota"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateManualQuota;

